import { create } from "zustand";
import type { Document, ExternalData, RenderableDocument } from "@easyblocks/core";

type RuntimeMode = "editor" | "render";
type EditorChromeMode = "auto" | "none" | "full";

// ========== EDITOR STATE ==========
export interface EditorState {
  // Live document being edited in the editor UI.
  currentDocument: Document | null;
  // Snapshot captured when the editor session starts (used for discard).
  baselineDocument: Document | null;
  // External data resolved for editor widgets/components.
  externalData: ExternalData;
  // Save/flush state for editor actions.
  isSaving: boolean;
  // Last save error surfaced to the UI.
  lastSaveError: Error | null;
  
  // Actions
  setCurrentDocument: (doc: Document | null) => void;
  setBaselineDocument: (doc: Document | null) => void;
  setExternalData: (data: ExternalData | ((prev: ExternalData) => ExternalData)) => void;
  setIsSaving: (saving: boolean) => void;
  setLastSaveError: (error: Error | null) => void;
  resetEditorState: () => void;
}

// ========== RENDER STATE ==========
export interface RenderState {
  // Renderable document built from the saved document + config.
  renderableDocument: RenderableDocument | null;
  // External data resolved for render view.
  renderExternalData: ExternalData;
  // Loading state for render build/resolve.
  renderLoading: boolean;
  
  // Actions
  setRenderableDocument: (doc: RenderableDocument | null) => void;
  setRenderExternalData: (data: ExternalData | ((prev: ExternalData) => ExternalData)) => void;
  setRenderLoading: (loading: boolean) => void;
  resetRenderState: () => void;
}

// ========== UI STATE ==========
export interface UIState {
  // Editor chrome behavior (auto = hide in iframe, full = always show).
  editorChromeMode: EditorChromeMode;
  // Render chrome visibility (controls header/footer around render view).
  renderShowChrome: boolean;
  // Delay used before save to allow auto-save to flush.
  saveDelayMs: number;
  
  // Actions
  setEditorChromeMode: (mode: EditorChromeMode) => void;
  setRenderShowChrome: (show: boolean) => void;
  setSaveDelayMs: (ms: number) => void;
}

// ========== APP CONTEXT STATE ==========
export interface AppContextState {
  // Resolved document id from mount or query params.
  documentId: string | null;
  // Active locale for build/render.
  locale: string | null;
  // Full document loaded from backend (for inspection or editor setup).
  activeDocument: Document | null;
  // True when mounting inside embedded editor/render roots.
  isEmbeddedMount: boolean;
  // True when the app is running inside an iframe.
  isInIframe: boolean;
  // Runtime mode resolved at startup.
  runtimeMode: RuntimeMode;

  // Actions
  setDocumentContext: (context: { documentId: string | null; locale: string | null }) => void;
  setActiveDocument: (doc: Document | null) => void;
  setIsEmbeddedMount: (isEmbedded: boolean) => void;
  setIsInIframe: (isInIframe: boolean) => void;
  setRuntimeMode: (mode: RuntimeMode) => void;
}

// ========== COMBINED STORE ==========
export interface AppStore extends EditorState, RenderState, UIState, AppContextState {}

const initialEditorState = {
  currentDocument: null,
  baselineDocument: null,
  externalData: {},
  isSaving: false,
  lastSaveError: null,
};

const initialRenderState = {
  renderableDocument: null,
  renderExternalData: {},
  renderLoading: false,
};

const initialUIState = {
  editorChromeMode: "auto" as EditorChromeMode,
  renderShowChrome: true,
  saveDelayMs: 2500,
};

const initialAppContextState = {
  documentId: null,
  locale: null,
  activeDocument: null,
  isEmbeddedMount: false,
  isInIframe: false,
  runtimeMode: "render" as RuntimeMode,
};

export const useAppStore = create<AppStore>((set) => ({
  // Editor state
  ...initialEditorState,
  
  setCurrentDocument: (doc) => set({ currentDocument: doc }),
  setBaselineDocument: (doc) => set({ baselineDocument: doc }),
  setExternalData: (data) =>
    set((state) => ({
      externalData: typeof data === "function" ? data(state.externalData) : data,
    })),
  setIsSaving: (saving) => set({ isSaving: saving }),
  setLastSaveError: (error) => set({ lastSaveError: error }),
  resetEditorState: () => set({ ...initialEditorState }),
  
  // Render state
  ...initialRenderState,
  
  setRenderableDocument: (doc) => set({ renderableDocument: doc }),
  setRenderExternalData: (data) =>
    set((state) => ({
      renderExternalData: typeof data === "function" ? data(state.renderExternalData) : data,
    })),
  setRenderLoading: (loading) => set({ renderLoading: loading }),
  resetRenderState: () => set({ ...initialRenderState }),
  
  // UI state
  ...initialUIState,
  
  setEditorChromeMode: (mode) => set({ editorChromeMode: mode }),
  setRenderShowChrome: (show) => set({ renderShowChrome: show }),
  setSaveDelayMs: (ms) => set({ saveDelayMs: ms }),

  // App context state
  ...initialAppContextState,
  setDocumentContext: (context) => set({ documentId: context.documentId, locale: context.locale }),
  setActiveDocument: (doc) => set({ activeDocument: doc }),
  setIsEmbeddedMount: (isEmbedded) => set({ isEmbeddedMount: isEmbedded }),
  setIsInIframe: (isInIframe) => set({ isInIframe }),
  setRuntimeMode: (mode) => set({ runtimeMode: mode }),
}));
